import React from 'react';
import { Building2, Target, Eye, Users } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#081221] to-[#0d63ce]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl font-bold text-white mb-6">
            Chi Siamo
          </h1>
          <p className="text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
            Una società leader nel settore fotovoltaico utility-scale, con oltre 20 anni di esperienza
            e un track record internazionale di eccellenza.
          </p>
        </div>
      </section>

      {/* Company Profile */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
                Profilo Aziendale
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                TerniEnergia Progetti S.r.l. nasce nel 2018 come unità specializzata nella progettazione e
                realizzazione di impianti fotovoltaici di grande taglia.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Nel corso degli anni abbiamo consolidato la nostra posizione come partner di
                riferimento per investitori istituzionali, fondi infrastrutturali e developer
                internazionali.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Con oltre 614 MWp installati in 5 paesi e un portafoglio di oltre 50 impianti in
                gestione O&M, rappresentiamo una realtà industriale solida e in crescita.
              </p>
            </div>
            <div className="relative">
              <img
                src="/photo2.jpeg"
                alt="Corporate Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-2xl opacity-20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-12 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6">
                <Target className="text-white" size={32} />
              </div>
              <h3 className="font-serif text-3xl font-bold text-[#081221] mb-6">Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Fornire soluzioni EPC, O&M e Asset Management di eccellenza per impianti fotovoltaici
                utility-scale, massimizzando il valore per gli investitori e contribuendo alla
                transizione energetica globale.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-12 shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={32} />
              </div>
              <h3 className="font-serif text-3xl font-bold text-[#081221] mb-6">Vision</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Diventare il partner di riferimento per la realizzazione e gestione
                di impianti fotovoltaici utility-scale, distinguendoci per innovazione, affidabilità
                e eccellenza operativa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-5xl font-bold text-[#081221] mb-16 text-center">
            La Nostra Storia
          </h2>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#0d63ce]/20" />

            {/* Timeline Items */}
            <div className="space-y-12">
              {[
                {
                  year: '2005',
                  title: 'Avvio attività',
                  description: "Avvio delle attività nel settore fotovoltaico da parte della TerniEnergia S.p.A. con operatività da leader di mercato in Italia."
                },
                {
                  year: '2018',
                  title: 'Costituzione',
                  description: "Costituzione di TerniEnergia Progetti S.r.l. da parte della TerniEnergia S.p.A. (successivamente AlgoWatt S.p.A.) mediante conferimento del ramo d'azienda fotovoltaico."
                },
                {
                  year: '2019-2023',
                  title: 'Sviluppo e consolidamento',
                  description: "Sviluppo e consolidamento delle attività EPC nel mercato italiano utility scale. Gestione delle attività di Operation & Maintenance tramite contratto di affitto di ramo d'azienda dalla controllante, con progressivo rafforzamento del track record industriale."
                },
                {
                  year: '2024',
                  title: 'Liquidazione giudiziale',
                  description: 'Liquidazione giudiziale della controllante AlgoWatt S.p.A.'
                },
                {
                  year: 'Mar 2025',
                  title: 'Acquisizione',
                  description: "Acquisizione della Società da parte di Terni Grandi Impianti S.r.l. e contestuale conferimento del ramo O&M in TEP e operazioni di ricostituzione del capitale sociale e rafforzamento della struttura patrimoniale."
                },
                {
                  year: 'PRESENTE',
                  title: 'Integrazione e consolidamento',
                  description: 'Integrazione del ramo O&M e accentramento delle attività EPC e O&M, con rafforzamento del presidio operativo e consolidamento nei servizi di gestione e revamping. Realizzazione di commesse utility scale e sottoscrizione di nuovi contratti.'
                }
              ].map((item, index) => (
                <div key={index} className="relative flex items-center">
                  <div className="hidden md:flex w-1/2 justify-end pr-12">
                    {index % 2 === 0 && (
                      <div className="text-right">
                        <div className="text-4xl font-bold text-[#0d63ce] mb-2">{item.year}</div>
                        <h4 className="font-serif text-2xl font-bold text-[#081221] mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Center Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-[#0d63ce] rounded-full border-4 border-white shadow-lg transform -translate-x-2 md:-translate-x-2" />

                  <div className="md:w-1/2 md:pl-12 pl-16">
                    {(index % 2 !== 0 || window.innerWidth < 768) && (
                      <div>
                        <div className="text-4xl font-bold text-[#0d63ce] mb-2">{item.year}</div>
                        <h4 className="font-serif text-2xl font-bold text-[#081221] mb-2">
                          {item.title}
                        </h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Organization */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              Modello Industriale
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un approccio integrato per l'intero ciclo di vita degli impianti fotovoltaici
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: 'EPC Full Scope',
                points: [
                  'Rete di partnership per individuazione siti e autorizzazioni',
                  'Progettazione tecnica e autorizzativa',
                  'Procurement e coordinamento forniture',
                  'Costruzione in qualità e sicurezza',
                  'Direzione e coordinamento fino al collaudo'
                ]
              },
              {
                icon: Users,
                title: 'O&M, Revamping e Repowering',
                points: [
                  'Focalizzazione su attività O&M con margini elevati',
                  'Integrazione soluzioni digitali',
                  'Gestione operativa con contratti pluriennali',
                  'Manutenzione ordinaria e straordinaria',
                  'Attività di revamping e repowering'
                ]
              },
              {
                icon: Target,
                title: 'BOP (Balance of Plant)',
                points: [
                  'Opere meccaniche ed elettriche infrastrutturali',
                  'Installazione e cablaggio quadri elettrici',
                  'Cabine di trasformazione e connessione rete',
                  'Operatività come sub-contractor o incarico diretto',
                  'Gestione tecnica e operativa del cantiere'
                ]
              }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#081221] mb-4">
                    {item.title}
                  </h3>
                  <ul className="space-y-2">
                    {item.points.map((point, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <span className="text-[#0d63ce] mt-1.5">•</span>
                        <span className="text-gray-700 text-sm leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
            Certificazioni
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Il nostro impegno per qualità e sicurezza
          </p>

          <div className="flex flex-col md:flex-row justify-center items-center gap-8">
            <div className="bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl p-12 shadow-lg w-full md:w-auto">
              <div className="text-6xl font-bold text-[#0d63ce] mb-4">ISO</div>
              <div className="text-2xl font-semibold text-[#081221] mb-2">9001:2015</div>
              <div className="text-gray-600">Sistema di Gestione Qualità</div>
            </div>

            <div className="bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl p-12 shadow-lg w-full md:w-auto">
              <div className="text-6xl font-bold text-[#0d63ce] mb-4">ISO</div>
              <div className="text-2xl font-semibold text-[#081221] mb-2">45001:2018</div>
              <div className="text-gray-600">Salute e Sicurezza sul Lavoro</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
